import { File, Input } from '~/components/common/forms';
import React, { useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { useAccountStatus, useAuth } from '~/auth';

import { AccountStatuses } from '~/auth/use-account-status';
import Axios from '~/utils/axios';
// import { CloseIconGray } from '~/svgs';
import FormSection from './form-section';
import classNames from 'classnames';
import { serialize } from 'object-to-formdata';
import { useForm } from 'react-hook-form';
import { sentryCaptureException } from '~/errors/sentry';
import { Button } from '../common/button';
import { ReportIcon } from '~/svgs/marketplace';
import VatSamplePreviewModal from './vat-sample-preview-modal';

type BusinessFilesFormType = {
   sectionClassName?: string;
   formClassName?: string;
   footerClassName?: string;
   fwdBtnHandler: () => void;
   fwdBtnTitle: React.ReactNode;
   cancelBtnTitle?: React.ReactNode;
   cancelBtnHandler?: () => void;
   isDomesticUser?: boolean;
   isNusukUser?: boolean;
   isProfileSwitchMode?: boolean;
};

export const BusinessOnboardFilesForm = ({
   sectionClassName,
   formClassName,
   fwdBtnHandler,
   fwdBtnTitle,
   cancelBtnTitle,
   footerClassName,
   cancelBtnHandler,
   isDomesticUser,
   isNusukUser,
   isProfileSwitchMode,
}: BusinessFilesFormType) => {
   const { translate } = useTranslate();
   const [loading, setLoading] = useState(false);
   const { user, updateUser } = useAuth();
   const { status } = useAccountStatus();
   const [showVatSample, setShowVatSample] = useState(false);
   const {
      cr_file,
      cr_number,
      cr_expire_date,
      tourism_expire_date,
      tourism_license_file,
      umrah_license_file,
      tourism_number,
      vat_reg_file,
      vat_number,
   } = user || {};
   const {
      register,
      handleSubmit,
      setError,
      formState: { errors },
      setValue,
   } = useForm<Record<string, any>>({
      defaultValues: {
         vat_number,
         cr_number,
         cr_expire_date,
         tourism_number,
         tourism_expire_date,
      },
   });
   const submitHandler = async (values: any) => {
      const data = { ...values };
      if (data.cr_file) data.cr_file = data.cr_file[0];
      if (data.vat_reg_file) data.vat_reg_file = data.vat_reg_file[0];
      if (data.tourism_license_file) data.tourism_license_file = data.tourism_license_file[0];
      if (data.umrah_license_file) data.umrah_license_file = data.umrah_license_file[0];
      data.international = !isDomesticUser && !isNusukUser;
      data.is_umrah = isNusukUser || false;
      try {
         setLoading(true);
         const form = serialize(data);
         form.append('_method', 'PATCH');

         const { data: response } = await Axios.post(
            `/hy-providers/v1/profile/update/legal-documents`,
            form
         );
         updateUser({ ...user, ...response.data });
         fwdBtnHandler();
      } catch (error: any) {
         sentryCaptureException(error, {
            level: 'fatal',
            tags: { journey: 'profile' },
         });
         if (error.response?.status === 422) {
            // Looping errors coming from backend (should be inside error)
            Object.entries(error.response?.data?.errors).map(([key, value], idx) => {
               setError(key as any, { message: value + '' }, { shouldFocus: idx === 0 });
            });
         }
         setLoading(false);
      }
   };

   const underReview = status === AccountStatuses.UnderReview;
   const filesIncomplete = status === AccountStatuses.FilesIncomplete;

   return (
      <FormSection
         title={isProfileSwitchMode ? '' : <Translate id="profile:legal documents" />}
         cancelBtnTitle={cancelBtnTitle}
         fwdBtnTitle={fwdBtnTitle}
         formClassName={classNames('xl:w-8/12', formClassName)}
         sectionClassName={sectionClassName}
         cancelBtnHandler={cancelBtnHandler}
         footerClassName={footerClassName}
         loading={loading}
         fwdBtnHandler={handleSubmit(submitHandler)}
         note={
            underReview && filesIncomplete && <Translate id="profile:legal documents info note" />
         }>
         <div>
            {/* <button
               onClick={() => cancelBtnHandler?.()}
               type="button"
               className="absolute z-10 rounded-full focus:outline-none focus:ring-0 top-10 ltr:right-8 rtl:left-8 ">
               <CloseIconGray />
            </button> */}

            <File
               id="cr_certificate"
               className="placeholder:!text-xs"
               label={
                  <div className="flex items-center space-x-1 rtl:space-x-reverse">
                     <div>
                        <Translate
                           id={`profile:${
                              isDomesticUser || isNusukUser
                                 ? 'cr certif'
                                 : 'international cr file label'
                           }`}
                        />
                     </div>
                  </div>
               }
               // description={<Translate id="profile:cr certif description" />}
               placeholder={translate({
                  id: `profile:${
                     isDomesticUser || isNusukUser
                        ? 'cr certif'
                        : 'international cr file placeholder'
                  }`,
               })}
               groupClassName="!mb-8 col-span-2"
               defaultImage={cr_file}
               {...register('cr_file', {
                  required: cr_file ? false : translate({ id: 'validation:required' }),
                  // required: translate({ id: 'validation:required' }),
               })}
               error={(errors.cr_file as any)?.message}
               onFileDelete={() => {
                  updateUser({ ...user, ...{ cr_file: '' } });
                  setValue('cr_file', undefined);
               }}
               setError={(message: string) => setError('cr_file', { message })}
               is_required
               isInputHidable={isProfileSwitchMode || false}
            />
            {isDomesticUser || isNusukUser ? (
               <>
                  {!isNusukUser ? (
                     <File
                        id="tourism_license"
                        className="placeholder:!text-xs"
                        label={
                           <div className="flex items-center space-x-1 rtl:space-x-reverse">
                              <div>
                                 <Translate id="profile:tourism license" />
                              </div>
                           </div>
                        }
                        description={<Translate id="profile:practice licence description" />}
                        placeholder={translate({ id: 'profile:legal files description' })}
                        groupClassName="!mb-8 col-span-2"
                        defaultImage={tourism_license_file}
                        error={(errors.tourism_license_file as any)?.message}
                        {...register('tourism_license_file', {
                           required: tourism_license_file
                              ? false
                              : translate({ id: 'validation:required' }),
                        })}
                        onFileDelete={() => {
                           updateUser({ ...user, ...{ tourism_license_file: '' } });
                           setValue('tourism_license_file', undefined);
                        }}
                        setError={(message: string) =>
                           setError('tourism_license_file', { message })
                        }
                        is_required
                     />
                  ) : (
                     <File
                        id="umrah_license_file"
                        className="placeholder:!text-xs"
                        label={
                           <div className="flex items-center space-x-1 rtl:space-x-reverse">
                              <div>
                                 <Translate id="profile:alhaj licence" />
                              </div>
                           </div>
                        }
                        placeholder={translate({ id: 'profile:legal files description' })}
                        groupClassName="!mb-8 col-span-2"
                        defaultImage={umrah_license_file}
                        error={(errors.umrah_license_file as any)?.message}
                        {...register('umrah_license_file', {
                           required: umrah_license_file
                              ? false
                              : translate({ id: 'validation:required' }),
                        })}
                        onFileDelete={() => {
                           updateUser({ ...user, ...{ umrah_license_file: '' } });
                           setValue('umrah_license_file', undefined);
                        }}
                        setError={(message: string) => setError('umrah_license_file', { message })}
                        is_required
                     />
                  )}
                  <File
                     id="vat_reg_file"
                     className="placeholder:!text-xs"
                     label={
                        <div className="flex items-center space-x-1 rtl:space-x-reverse">
                           <div>
                              <Translate id="profile:vat or exemption certificate" />
                           </div>
                        </div>
                     }
                     description={<Translate id="profile:vat cert description" />}
                     placeholder={translate({ id: 'profile:legal files description' })}
                     groupClassName="!mb-8 col-span-2"
                     defaultImage={vat_reg_file}
                     error={(errors.vat_reg_file as any)?.message}
                     {...register('vat_reg_file', {
                        required: vat_reg_file ? false : translate({ id: 'validation:required' }),
                     })}
                     onFileDelete={() => {
                        updateUser({ ...user, ...{ vat_reg_file: '' } });
                        setValue('vat_reg_file', undefined);
                     }}
                     setError={(message: string) => {
                        console.log(message);
                        setError('vat_reg_file', { message });
                     }}
                     is_required
                     isInputHidable={isProfileSwitchMode || false}
                  />
                  <Button
                     className="!p-0 !font-normal gap-3 text-primary"
                     onClick={() => setShowVatSample(true)}>
                     <ReportIcon className="w-5 h-5 [&>*]:fill-primary" />
                     <span className="">
                        <Translate id="onboard:view VAT exemption sample" />
                     </span>
                  </Button>
                  {!isProfileSwitchMode ? (
                     <Input
                        label={translate({ id: 'profile:business name' })}
                        placeholder={translate({ id: 'profile:business name placeholder' })}
                        description={<Translate id="profile:business name description" />}
                        type="text"
                        id="business_name"
                        error={errors.business_name?.message}
                        {...register('business_name', {
                           required: translate({ id: 'validation:required' }),
                        })}
                        isRequired
                     />
                  ) : null}
                  <VatSamplePreviewModal open={showVatSample} setOpen={setShowVatSample} />
               </>
            ) : null}
         </div>
      </FormSection>
   );
};
